/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { DEFAULT_LANGUAGE, type SupportedLanguage } from './language.utils';

/**
 * Form doğrulama ve işlem mesajları için çeviri sözlüğü
 */
export const validationTranslations: Record<string, Record<SupportedLanguage, string>> = {
    required: {
        tr: 'Bu alan gereklidir',
        en: 'This field is required',
        ar: 'هذا الحقل مطلوب',
        de: 'Dieses Feld ist erforderlich',
        fr: 'Ce champ est obligatoire',
        es: 'Este campo es obligatorio',
        it: 'Questo campo è obbligatorio',
        ru: 'Это поле обязательно к заполнению',
        zh: '此字段是必需的',
        ja: 'この項目は必須です',
    },
    invalidEmail: {
        tr: 'Geçerli bir e-posta adresi giriniz',
        en: 'Please enter a valid email address',
        ar: 'يرجى إدخال عنوان بريد إلكتروني صحيح',
        de: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
        fr: 'Veuillez saisir une adresse e-mail valide',
        es: 'Por favor, introduzca una dirección de correo electrónico válida',
        it: 'Inserisci un indirizzo email valido',
        ru: 'Пожалуйста, введите корректный адрес электронной почты',
        zh: '请输入有效的电子邮件地址',
        ja: '有効なメールアドレスを入力してください',
    },
    operationSuccess: {
        tr: 'İşlem başarıyla tamamlandı',
        en: 'Operation completed successfully',
        ar: 'تمت العملية بنجاح',
        de: 'Vorgang erfolgreich abgeschlossen',
        fr: 'Opération terminée avec succès',
        es: 'Operación completada con éxito',
        it: 'Operazione completata con successo',
        ru: 'Операция успешно завершена',
        zh: '操作成功完成',
        ja: '操作が正常に完了しました',
    },
    operationFailed: {
        tr: 'İşlem başarısız oldu',
        en: 'Operation failed',
        ar: 'فشلت العملية',
        de: 'Vorgang fehlgeschlagen',
        fr: 'L’opération a échoué',
        es: 'La operación ha fallado',
        it: 'Operazione fallita',
        ru: 'Операция не удалась',
        zh: '操作失败',
        ja: '操作に失敗しました',
    },
    savedSuccessfully: {
        tr: 'Başarıyla kaydedildi',
        en: 'Saved successfully',
        ar: 'تم الحفظ بنجاح',
        de: 'Erfolgreich gespeichert',
        fr: 'Enregistré avec succès',
        es: 'Guardado con éxito',
        it: 'Salvato con successo',
        ru: 'Успешно сохранено',
        zh: '保存成功',
        ja: '正常に保存されました',
    },
    deletedSuccessfully: {
        tr: 'Başarıyla silindi',
        en: 'Deleted successfully',
        ar: 'تم الحذف بنجاح',
        de: 'Erfolgreich gelöscht',
        fr: 'Supprimé avec succès',
        es: 'Eliminado con éxito',
        it: 'Eliminato con successo',
        ru: 'Успешно удалено',
        zh: '删除成功',
        ja: '正常に削除されました',
    },
    onlyImageAllowed: {
        tr: 'Sadece resim dosyaları yüklenebilir',
        en: 'Only image files can be uploaded',
        ar: 'يمكن تحميل ملفات الصور فقط',
        de: 'Es können nur Bilddateien hochgeladen werden',
        fr: 'Seuls les fichiers image peuvent être téléchargés',
        es: 'Solo se pueden subir archivos de imagen',
        it: 'È possibile caricare solo file immagine',
        ru: 'Можно загружать только изображения',
        zh: '只能上传图像文件',
        ja: '画像ファイルのみアップロードできます',
    },
    fileSizeLimit: {
        tr: "Dosya boyutu {size} 'dan küçük olmalıdır",
        en: 'File size must be less than {size}',
        ar: 'يجب أن يكون حجم الملف أقل من {size}',
        de: 'Die Dateigröße muss weniger als {size} betragen',
        fr: 'La taille du fichier doit être inférieure à {size}',
        es: 'El tamaño del archivo debe ser inferior a {size}',
        it: 'La dimensione del file deve essere inferiore a {size}',
        ru: 'Размер файла должен быть меньше {size}',
        zh: '文件大小必须小于 {size}',
        ja: 'ファイルサイズは {size} 未満である必要があります',
    },
    failedToLoad: {
        tr: 'Veriler yüklenemedi',
        en: 'Failed to load data',
        ar: 'فشل تحميل البيانات',
        de: 'Daten konnten nicht geladen werden',
        fr: 'Échec du chargement des données',
        es: 'Error al cargar los datos',
        it: 'Impossibile caricare i dati',
        ru: 'Не удалось загрузить данные',
        zh: '加载数据失败',
        ja: 'データの読み込みに失敗しました',
    },
    deleteConfirm: {
        tr: 'Bu kaydı silmek istediğinizden emin misiniz?',
        en: 'Are you sure you want to delete this record?',
        ar: 'هل أنت متأكد أنك تريد حذف هذا السجل؟',
        de: 'Sind Sie sicher, dass Sie diesen Datensatz löschen möchten?',
        fr: 'Êtes-vous sûr de vouloir supprimer cet enregistrement ?',
        es: '¿Está seguro de que desea eliminar este registro?',
        it: 'Sei sicuro di voler eliminare questo record?',
        ru: 'Вы уверены, что хотите удалить эту запись?',
        zh: '您确定要删除此记录吗？',
        ja: 'このレコードを削除してもよろしいですか？',
    }
};

/**
 * Belirtilen anahtar ve dil için çeviriyi döndürür
 */
export function getValidationMessage(
    key: keyof typeof validationTranslations,
    lang: SupportedLanguage,
    params?: Record<string, string | number>
): string {
    let message = validationTranslations[key]?.[lang] || validationTranslations[key]?.[DEFAULT_LANGUAGE] || key;

    if (params) {
        Object.entries(params).forEach(([k, v]) => {
            message = message.replace(`{${k}}`, String(v));
        });
    }

    return message;
}
